import { zodResolver } from '@hookform/resolvers/zod';
import { always, any, anyPass, cond, endsWith, equals, filter, includes, isNil, or } from 'ramda';
import React, { FC, useContext, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import BackAndNextNav from '@ui/molecules/BackAndNextNav';
import SpacedGridItem from '@ui/molecules/SpacedGridItem';

import MedicationSelection, {
	IMedInfo,
	IMedication,
	IRxCategoryRouteAndMeds,
	isDefaultMedOption,
} from '@ui/templates/MedicationSelection';

import { medicationDeliveryConstants } from '@ui/routes/shared/MedicationDeliveryStep';

import spaced from '@ui/styled/Spaced';

import useExcludedCallback from '@hooks/useExcludedCallback';

import { exists, getValuesFromData } from '@utils/general';

import FlowContext from '@providers/FlowContextProvider';

import { DeliveryTypes } from '@folx-types/delivery';
import { FormData, IFlowStepComponentProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

import { dataConstants, schema } from './MemberTestosteroneMedicationSelectionStep.data';

export interface IMedicationSelectionStepPageContent {
	pageHeader: string;
	pageDetails: string;
	routes: {
		routeHeader: string;
		routeTypes: IMedication[];
	};
	injectables: {
		injectableMedicationHeader: 'Injectable medication';
		injectableTypeHeader: 'Injection type';
		injectableMedications: IMedication[];
		injectableTypes: IMedication[];
	};
}

interface IMedicationSelectionStepProps extends IFlowStepComponentProps {
	loading: boolean;
	pageContent: IMedicationSelectionStepPageContent;
	routeMeds?: string[];
	rxRouteData?: IRxCategoryRouteAndMeds;
	handleMedSelection?: (data: FormData, currentMed: IMedInfo) => void;
	shouldAnimate?: boolean;
}

const FlexibleColumn = spaced(FlexColumn);

const HeaderContainer = styled(FlexColumn)`
	margin-bottom: ${theme.spacing.lgxl};

	${HeadingText} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const MemberTestosteroneMedicationSelectionStep: FC<IMedicationSelectionStepProps> = ({
	handleMedSelection = () => {
		// empty
	},
	pageContent,
	routeMeds = undefined,
	stepKey,
	shouldAnimate = true,
}) => {
	console.log('MemberTestosteroneMedicationSelectionStep.component');
	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const { pageHeader, pageDetails, routes, injectables } = pageContent;
	const {
		injectableMedicationHeader,
		injectableTypeHeader,
		injectableMedications,
		injectableTypes,
	} = injectables;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: getValuesFromData(dataConstants, existingFormData),
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const { handleSubmit, watch, setValue } = form;

	const [submitting, setSubmitting] = useState(false);

	const [routeAnimating, setRouteAnimating] = useState(false);
	const [medicationAnimating, setMedicationAnimating] = useState(false);

	const routeSelection = watch<string>(dataConstants.ROUTE, null);
	const medicationSelection = watch<string>(dataConstants.MEDICATION, '');
	const typeSelection = watch<string>(dataConstants.TYPE, null);

	const delivery: DeliveryTypes | undefined =
		existingFormData?.[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD];

	useExcludedCallback<string>({
		value: existingFormData?.[dataConstants.MEDICATION],
		list: routeMeds,
		callback: (value) => {
			if (isDefaultMedOption(`${value}`)) {
				resetSelections('type');
				return;
			}
			resetSelections('medication');
		},
	});

	useExcludedCallback<string>({
		value: existingFormData?.[dataConstants.ROUTE],
		list: routeMeds,
		callback: (value) => {
			if (isDefaultMedOption(`${value}`)) {
				resetSelections('medication');
				return;
			}
			resetSelections('route');
		},
	});

	const resetSelections = (selectionLevel: 'route' | 'medication' | 'type') => {
		setValue(dataConstants.CUSTOM, null, { shouldValidate: true });

		setValue(dataConstants.TYPE, null, { shouldValidate: true });
		if (selectionLevel === 'type') {
			return;
		}

		setValue(dataConstants.MEDICATION, null, { shouldValidate: true });
		if (selectionLevel === 'medication') {
			return;
		}

		setValue(dataConstants.ROUTE, null, { shouldValidate: true });
	};

	const filterRoutes = (routeTypes: IMedication[], availableRoutes?: string[]) => {
		if (isNil(availableRoutes)) {
			return routeTypes;
		}
		return filter<IMedication>((routeType) => {
			return anyPass([
				(key: string) => {
					return includes(key, availableRoutes);
				},
				(key: string) => {
					return isDefaultMedOption(key);
				},
				(key: string) => {
					const isIncluded = filter<string>((availableRoute: string) => {
						return includes(key, availableRoute);
					});
					return any(exists)(isIncluded(availableRoutes));
				},
			])(routeType.key);
		})(routeTypes);
	};

	const filterInjectableMeds = (injectables: IMedication[], availableMeds?: string[]) => {
		if (isNil(availableMeds)) {
			return injectables;
		}
		return filter<IMedication>((injectableMed) => {
			return or(
				includes(injectableMed.key, availableMeds),
				endsWith('.dont_know', injectableMed.key)
			);
		})(injectables);
	};

	const handleSelection = (selectionLevel: 'route' | 'medication' | 'type', newValue?: string) => {
		const meds: { data: FormData; info: IMedInfo } = cond([
			[
				equals('route'),
				always<{ data: FormData; info: IMedInfo }>({
					data: {
						[dataConstants.ROUTE]: newValue,
						[dataConstants.MEDICATION]: null,
						[dataConstants.TYPE]: null,
					},
					info: {
						route: newValue,
						medication: null,
						type: null,
						frequency: null,
					},
				}),
			],
			[
				equals('medication'),
				always<{ data: FormData; info: IMedInfo }>({
					data: {
						[dataConstants.ROUTE]: routeSelection,
						[dataConstants.MEDICATION]: newValue,
						[dataConstants.TYPE]: null,
					},
					info: {
						route: routeSelection,
						medication: newValue,
						type: null,
						frequency: null,
					},
				}),
			],
			[
				equals('type'),
				always<{ data: FormData; info: IMedInfo }>({
					data: {
						[dataConstants.ROUTE]: routeSelection,
						[dataConstants.MEDICATION]: medicationSelection,
						[dataConstants.TYPE]: newValue,
					},
					info: {
						route: routeSelection,
						medication: medicationSelection,
						type: newValue,
						frequency: null,
					},
				}),
			],
		])(selectionLevel);

		handleMedSelection(meds.data, meds.info);
	};

	const renderRouteSelection = () => {
		const filteredRoutes = filterRoutes(routes.routeTypes, routeMeds);
		return (
			<MedicationSelection
				type="route"
				name={dataConstants.ROUTE}
				header={routes.routeHeader}
				selection={routeSelection}
				options={filteredRoutes}
				delivery={delivery}
				extraText={{
					priceText: '/mo',
				}}
				handleEdit={() => {
					resetSelections('route');
					handleSelection('route');
				}}
				handleSelection={(value) => {
					handleSelection('route', value);
				}}
				handleAnimating={(animating) => {
					setRouteAnimating(animating);
				}}
				shouldAnimate={shouldAnimate}
			/>
		);
	};

	const renderInjectableMeds = () => {
		const filteredMeds = filterInjectableMeds(injectableMedications, routeMeds);
		return (
			<MedicationSelection
				visible={!routeAnimating}
				type="med"
				name={dataConstants.MEDICATION}
				header={injectableMedicationHeader}
				selection={medicationSelection}
				parentKey={routeSelection}
				options={filteredMeds}
				delivery={delivery}
				extraText={{
					priceText: '/mo',
				}}
				handleEdit={() => {
					resetSelections('medication');
					handleSelection('medication');
				}}
				handleSelection={(value) => {
					handleSelection('medication', value);
				}}
				handleAnimating={(animating) => {
					setMedicationAnimating(animating);
				}}
				shouldAnimate={shouldAnimate}
			/>
		);
	};

	const renderInjectableTypes = () => {
		return (
			<MedicationSelection
				visible={!medicationAnimating}
				type="type"
				name={dataConstants.TYPE}
				header={injectableTypeHeader}
				parentKey={routeSelection}
				options={injectableTypes}
				handleEdit={() => {
					resetSelections('type');
					handleSelection('type');
				}}
				handleSelection={(value) => {
					handleSelection('type', value);
				}}
				shouldAnimate={shouldAnimate}
				selection={typeSelection}
			/>
		);
	};

	const injectableSelected = equals(routeSelection, 'hrt_t.injectable');
	const injectableMedDontKnow = equals(medicationSelection, 'hrt_t.injectable.dont_know');

	const headerRef = useRef<HTMLDivElement>(null);

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		await handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<>
			<SpacedGridItem marginBottom="lgxl">
				<HeaderContainer ref={headerRef}>
					<HeadingText>{pageHeader}</HeadingText>
					<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageDetails }} />
				</HeaderContainer>
				<form onSubmit={handleSubmit(onSubmit)}>
					<FormProvider {...form}>
						<FlexibleColumn gap="lg" marginBottom="xxl">
							{renderRouteSelection()}

							{injectableSelected && renderInjectableMeds()}
							{injectableSelected &&
								!injectableMedDontKnow &&
								medicationSelection &&
								renderInjectableTypes()}

							<BackAndNextNav
								marginTop="xxl"
								marginBottom="xxxl"
								submitLabel="Next"
								backOnClick={() => handleNavBack(stepKey)}
								submitting={submitting}
							/>
						</FlexibleColumn>
					</FormProvider>
				</form>
			</SpacedGridItem>
		</>
	);
};

export default MemberTestosteroneMedicationSelectionStep;
